import React, { useState, useEffect, useCallback } from 'react';
import { preferencesAPI, exportAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Download, 
  FileSpreadsheet, 
  FileText,
  Settings2,
  Filter,
  X,
  Loader2,
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * Advanced DataTable component with sorting, filtering, column customization, and export
 * 
 * @param {Object} props
 * @param {string} props.module - Module name for preferences and export (e.g., "indents", "purchase_orders")
 * @param {Array} props.data - Array of data objects
 * @param {Array} props.columns - Column definitions [{key, label, sortable, filterable, render, width}]
 * @param {Function} props.onRowClick - Optional row click handler
 * @param {Function} props.renderActions - Optional function to render action buttons for each row
 * @param {string} props.title - Title for exports
 * @param {boolean} props.loading - Loading state
 */
export const DataTable = ({
  module,
  data = [],
  columns = [],
  onRowClick,
  renderActions,
  title = "Data Export",
  loading = false,
}) => {
  // State
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [visibleColumns, setVisibleColumns] = useState(columns.map(c => c.key));
  const [columnOrder, setColumnOrder] = useState(columns.map(c => c.key));
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Load user preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await preferencesAPI.get(module);
        const prefs = response.data;
        if (prefs && prefs.visible_columns && prefs.visible_columns.length > 0) {
          setVisibleColumns(prefs.visible_columns);
          setColumnOrder(prefs.column_order || prefs.visible_columns);
          if (prefs.default_sort_column) {
            setSortColumn(prefs.default_sort_column);
            setSortDirection(prefs.default_sort_direction || 'asc');
          }
          if (prefs.filters) {
            setFilters(prefs.filters);
            if (prefs.filters.date_range) {
              setDateRange(prefs.filters.date_range);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      } finally {
        setPrefsLoaded(true);
      }
    };
    
    if (module) {
      loadPreferences();
    } else {
      setPrefsLoaded(true);
    }
  }, [module]);

  // Save preferences
  const savePreferences = useCallback(async () => {
    if (!module) return;
    
    try {
      await preferencesAPI.update(module, {
        visible_columns: visibleColumns,
        column_order: columnOrder,
        default_sort_column: sortColumn,
        default_sort_direction: sortDirection,
        filters: { ...filters, date_range: dateRange }
      });
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    }
  }, [module, visibleColumns, columnOrder, sortColumn, sortDirection, filters, dateRange]);

  // Handle sort
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Handle column visibility toggle
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(k => k !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  // Sort and filter data
  const processedData = React.useMemo(() => {
    let result = [...data];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(row => 
        Object.values(row).some(val => 
          String(val).toLowerCase().includes(term)
        )
      );
    }
    
    // Apply date range filter
    if (dateRange.start || dateRange.end) {
      result = result.filter(row => {
        const dateFields = ['created_at', 'date', 'expense_date', 'check_in'];
        for (const field of dateFields) {
          if (row[field]) {
            const rowDate = row[field].substring(0, 10);
            if (dateRange.start && rowDate < dateRange.start) return false;
            if (dateRange.end && rowDate > dateRange.end) return false;
            return true;
          }
        }
        return true;
      });
    }
    
    // Apply column filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== 'date_range') {
        result = result.filter(row => {
          const rowValue = String(row[key] || '').toLowerCase();
          return rowValue.includes(String(value).toLowerCase());
        });
      }
    });
    
    // Sort
    if (sortColumn) {
      result.sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        
        // Handle null/undefined
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        
        // Numeric comparison
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        // String comparison
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        
        if (sortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      });
    }
    
    return result;
  }, [data, searchTerm, dateRange, filters, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(processedData.length / pageSize));
  React.useEffect(() => {
    // Reset to first page whenever the result set or page size changes
    setCurrentPage(1);
  }, [pageSize, searchTerm, dateRange, filters, sortColumn, sortDirection, data.length]);
  const safePage = Math.min(currentPage, totalPages);
  const paginatedData = React.useMemo(
    () => processedData.slice((safePage - 1) * pageSize, safePage * pageSize),
    [processedData, safePage, pageSize]
  );
  const startRecord = processedData.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endRecord = Math.min(safePage * pageSize, processedData.length);

  // Export handlers
  const handleExport = async (format) => {
    try {
      setExporting(true);
      
      const exportRequest = {
        module,
        format,
        filters: { ...filters, date_range: dateRange.start || dateRange.end ? dateRange : null },
        columns: visibleColumns,
        sort_column: sortColumn,
        sort_direction: sortDirection,
        title
      };
      
      const response = await exportAPI.export(exportRequest);
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${module}_export_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setDateRange({ start: '', end: '' });
    setFilters({});
  };

  // Get visible columns in order
  const orderedVisibleColumns = columnOrder
    .filter(key => visibleColumns.includes(key))
    .map(key => columns.find(c => c.key === key))
    .filter(Boolean);

  const hasActiveFilters = searchTerm || dateRange.start || dateRange.end || Object.values(filters).some(v => v);

  if (!prefsLoaded) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Search and Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          
          {/* Date Range Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn(dateRange.start || dateRange.end ? "border-primary" : "")}>
                <Calendar className="h-4 w-4 mr-2" />
                Date Range
                {(dateRange.start || dateRange.end) && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {dateRange.start || '...'} - {dateRange.end || '...'}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => setDateRange({ start: '', end: '' })}
                >
                  Clear Dates
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Column Settings */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Visible Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns.map(column => (
                <DropdownMenuCheckboxItem
                  key={column.key}
                  checked={visibleColumns.includes(column.key)}
                  onCheckedChange={() => toggleColumn(column.key)}
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={savePreferences}>
                Save as Default
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Export */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting}>
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export Format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport('excel')}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <FileText className="h-4 w-4 mr-2 text-red-600" />
                PDF Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {processedData.length} of {data.length} records
        {hasActiveFilters && " (filtered)"}
      </div>
      
      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {orderedVisibleColumns.map(column => (
                  <TableHead 
                    key={column.key}
                    style={{ width: column.width }}
                    className={cn(
                      column.sortable !== false && "cursor-pointer hover:bg-muted/50 select-none",
                      "whitespace-nowrap"
                    )}
                    onClick={() => column.sortable !== false && handleSort(column.key)}
                  >
                    <div className="flex items-center gap-1">
                      {typeof column.label === 'string' ? column.label : column.label}
                      {column.sortable !== false && (
                        <span className="ml-1">
                          {sortColumn === column.key ? (
                            sortDirection === 'asc' ? (
                              <ArrowUp className="h-4 w-4 text-primary" />
                            ) : (
                              <ArrowDown className="h-4 w-4 text-primary" />
                            )
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />
                          )}
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
                {renderActions && <TableHead className="w-24">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={orderedVisibleColumns.length + (renderActions ? 1 : 0)} className="h-32 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : processedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={orderedVisibleColumns.length + (renderActions ? 1 : 0)} className="h-32 text-center text-muted-foreground">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, rowIndex) => (
                  <TableRow 
                    key={row.id || rowIndex}
                    className={cn(onRowClick && "cursor-pointer hover:bg-muted/50")}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {orderedVisibleColumns.map(column => (
                      <TableCell key={column.key}>
                        {column.render ? column.render(row[column.key], row) : (
                          <span className="truncate max-w-xs block">
                            {row[column.key] ?? '-'}
                          </span>
                        )}
                      </TableCell>
                    ))}
                    {renderActions && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {renderActions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && processedData.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1" data-testid="datatable-pagination">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Rows per page:</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-[80px] h-8" data-testid="rows-per-page-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 40, 50, 100].map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="ml-2">{startRecord}-{endRecord} of {processedData.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(1)} disabled={safePage === 1} data-testid="page-first">
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} data-testid="page-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-3 text-sm font-medium" data-testid="page-info">Page {safePage} of {totalPages}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} data-testid="page-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages} data-testid="page-last">
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
